import lodash from 'lodash';
import { useIntl } from 'react-intl';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import ObjectWidget from '@plone/volto/components/manage/Widgets/ObjectWidget';

export const schemaExtender = (
  schema: object & {
    properties: any;
    conditions?: any;
    omit?: any;
    fieldsets: any;
  },
  data: any,
  intl: any,
) => {
  const mutated = lodash.cloneDeep(schema);

  Object.keys(schema.properties).forEach((key) => {
    const localizedInitialPath =
      schema.properties[key]?.widgetOptions?.frontendOptions
        ?.localizedInitialPath;
    if (!localizedInitialPath) {
      return;
    }
    mutated.properties[key].initialPath = localizedInitialPath?.[intl.locale];
    // We now have a function to set the initialPath dynamically
    // later this will enable us to also do stuff like /language/university/persons
    mutated.properties[key].initialPathFn = () =>
      localizedInitialPath?.[intl.locale];
  });

  if (!schema?.conditions && !schema?.omit) {
    return mutated;
  }

  if (schema?.conditions) {
    Object.keys(schema.conditions).forEach((key) => {
      Object.keys(schema.conditions[key]).forEach((field) => {
        const assert = schema.conditions[key][field];
        let value = data[field];
        if (typeof value === 'object' && value !== null && 'token' in value) {
          value = value.token;
        }
        if (
          (typeof assert === 'boolean' &&
            typeof value !== 'boolean' &&
            !!value !== assert) ||
          (((typeof assert !== 'boolean' && !Array.isArray(assert)) ||
            typeof assert === typeof value) &&
            value !== assert) ||
          (Array.isArray(assert) && !assert.includes(value))
        ) {
          delete mutated.properties[key];
        }
      });
    });
  }
  if (schema?.omit) {
    schema.omit.forEach((key: string) => {
      delete mutated.properties[key];
    });
  }
  mutated.fieldsets[0].fields = mutated.fieldsets[0].fields.filter(
    (item: string) => item in mutated.properties,
  );
  return mutated;
};

/**
 * Fills in the schema defaults of the fields that a condition has just
 * revealed and that carry no stored value yet.
 *
 * Volto's ObjectWidget renders `value?.[field]` and ignores the `default` the
 * backend puts into the sub-schema, so a flag the renderer treats as "on when
 * unset" showed up as an empty checkbox (#1530). Only condition-guarded fields
 * are seeded: a field like `show` — visible unconditionally — must keep
 * reflecting its stored state, otherwise the form would claim a feature the
 * page does not render.
 *
 * Display-only: nothing is written back here, the seeded value simply travels
 * with the next change the editor makes.
 */
export const applyConditionalDefaults = (
  schema: { properties: any; conditions?: any },
  value: Record<string, any>,
) => {
  if (!schema?.conditions) {
    return value;
  }

  const seeded = Object.keys(schema.conditions).reduce(
    (acc: Record<string, any>, key) => {
      const property = schema.properties?.[key];
      // Missing property means the condition filtered it out — it is not on
      // screen, so there is nothing to reflect. `== null` covers both shapes
      // of "unset": absent in freshly typed data, explicit `null` in what
      // plone.restapi serializes for a stored object.
      if (!property || property.default == null || value[key] != null) {
        return acc;
      }
      return { ...acc, [key]: property.default };
    },
    {},
  );

  return Object.keys(seeded).length ? { ...value, ...seeded } : value;
};

export interface CustomObjectWidgetProps {
  id: string;
  value?: object;
  default?: object;
  schema: object & {
    title: string;
  };
  onChange: (id: string, value: object) => void;
  block?: object;
}

const CustomObjectWidget: React.FC<CustomObjectWidgetProps> = (props) => {
  const {
    value,
    default: defaultValue,
    schema,
    onChange,
    block,
    ...rest
  } = props;
  const intl = useIntl();

  const objectValue = value || defaultValue || {};
  const extendedSchema = schemaExtender(schema as any, objectValue, intl);

  return (
    <div className="custom-object-widget">
      {schema && (
        <FormFieldWrapper title={schema.title} id={props.id}>
          <ObjectWidget
            schema={extendedSchema}
            {...rest}
            value={applyConditionalDefaults(extendedSchema, objectValue)}
            onChange={(id: string, value: object) => onChange(id, value)}
            block={block}
          />
        </FormFieldWrapper>
      )}
    </div>
  );
};

export default CustomObjectWidget;
